import { useState, useRef, useEffect } from 'react';
import JSZip from 'jszip';
import { v4 as uuidv4 } from 'uuid';

/**
 * Custom hook for handling file input with validation and preview generation
 * @param {Object} config - Configuration object
 * @param {number} config.maxFileSizeMb - Maximum file size in MB
 * @param {string} config.fileType - Accepted file type (e.g., 'video/mp4', 'image/')
 * @param {boolean} config.multiple - Whether to allow multiple file selection
 * @param {number} config.previewThreshold - Max file size in MB for preview generation
 * @returns {Object} File input state and handlers
 */
const useFileInput = ({
  maxFileSizeMb,
  fileType,
  multiple = false,
  previewThreshold = 500,
}) => {
  const [file, setFile] = useState({});
  const [fileURL, setFileURL] = useState('');
  const [fileError, setFileError] = useState('');
  const fileInputRef = useRef(null);
  const blobURLRef = useRef(null); // Track blob URL for cleanup

  const [attachedFiles, setAttachedFiles] = useState([]);

  useEffect(() => {
    return () => {
      // Revoke blob URL on unmount to free memory
      if (blobURLRef.current) {
        URL.revokeObjectURL(blobURLRef.current);
        blobURLRef.current = null;
      }
    };
  }, []);

  const validateInteractiveFile = async (selectedFile, indexName) => {
    const zipFile = await JSZip.loadAsync(selectedFile);
    const zipEntries = Object.keys(zipFile.files);
    const directories = [];
    const zipFiles = [];

    zipEntries.forEach((entry) => {
      if (!entry.startsWith('__MACOSX')) {
        if (!directories.includes(entry.split('/')[0]))
          directories.push(entry.split('/')[0]);
        zipFiles.push(entry.split('/')[1]);
      }
    });

    if (directories.length !== 1) {
      setFileError('The zip file should contain only one main folder.');
      return false;
    }

    const rootDir = directories[0];

    let targetFile;
    let searchPath;

    // Checks first if a sub-directory is included
    if (indexName.includes('/')) {
      const pathParts = indexName.split('/');
      const subdirectory = pathParts[0];
      const fileName = pathParts[pathParts.length - 1];

      searchPath = `${rootDir}/${subdirectory}/`;
      targetFile = fileName;

      const subdirectoryExists = zipEntries.some((entry) =>
        entry.startsWith(searchPath)
      );
      const fileInSubdirectory = zipEntries.some(
        (entry) => entry === `${searchPath}${targetFile}`
      );

      if (!subdirectoryExists) {
        setFileError(`The subdirectory "${subdirectory}" was not found.`);
        return false;
      }

      if (!fileInSubdirectory) {
        setFileError(
          `The specified index file "${targetFile}" was not found in the "${rootDir}/${subdirectory}" folder inside the zip file.`
        );
        return false;
      }
    } else {
      // Finds the entry point in the root directory
      targetFile = indexName;
      const fileInRoot = zipEntries.some(
        (entry) => entry === `${rootDir}/${targetFile}`
      );

      if (!fileInRoot) {
        setFileError(
          `The specified index file "${indexName}" was not found in the "${rootDir}" folder inside the zip file.`
        );
        return false;
      }
    }

    const zipFileName = selectedFile.name.replace(/\.zip$/i, '');
    if (zipFileName !== rootDir) {
      setFileError(
        `The zip file name "${zipFileName}" and the folder name "${rootDir}" inside the zip file do not match. They need to be identical.`
      );
      return false;
    }

    return true;
  };

  const setErrorMessage = (error) => {
    setFileError(error);

    setTimeout(() => {
      setFileError('');
    }, 3000);

    if (!multiple) {
      setFile({});
    }
  };

  const handleAttachFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleRemoveSelectedFile = () => {
    // Revoke previous blob URL if it exists
    if (blobURLRef.current) {
      URL.revokeObjectURL(blobURLRef.current);
      blobURLRef.current = null;
    }
    setFile({});
    setFileURL('');
  };

  const removeAttachedFileFromFiles = (fileId) => {
    setAttachedFiles((prev) =>
      prev.filter((fileItem) => fileItem.id !== fileId)
    );
  };

  const clearAllFiles = () => {
    setAttachedFiles([]);
  };

  const handleFileInputChange = (event) => {
    // for multiple files
    if (multiple) {
      const selectedFiles = Array.from(event.target.files);

      if (selectedFiles.length === 0) return;

      const validFiles = [];

      selectedFiles.forEach((targetFile) => {
        const fileSizeInMB = targetFile.size / (1024 * 1024);

        if (fileSizeInMB > maxFileSizeMb) {
          if (fileSizeInMB >= 1000 && maxFileSizeMb >= 1000) {
            setErrorMessage(
              `File "${targetFile.name}" exceeds ${maxFileSizeMb} GB limit.`
            );
          } else {
            setErrorMessage(
              `File "${targetFile.name}" exceeds ${maxFileSizeMb} MB limit.`
            );
            return;
          }
        }

        if (!targetFile.type.startsWith(fileType)) {
          setErrorMessage(
            `File "${targetFile.name}" is not a valid ${fileType} file.`
          );
          return;
        }

        validFiles.push({
          id: uuidv4(),
          name: targetFile.name.replace(/\.zip$/i, ''),
          file: targetFile,
          size: targetFile.size,
        });
      });

      if (validFiles.length > 0) {
        setAttachedFiles((prev) => {
          const newFiles = [...prev, ...validFiles];
          return newFiles;
        });
      }

      event.target.value = null;
    }
    // for single file
    else {
      const targetFile = event.target.files[0];

      if (targetFile) {
        const fileSizeInMB = targetFile.size / (1024 * 1024);

        if (fileSizeInMB > maxFileSizeMb) {
          setErrorMessage(
            `File size exceeds ${maxFileSizeMb} MB limit. Please select a smaller file.`
          );
        } else if (!targetFile.type.startsWith(fileType)) {
          setErrorMessage(`Please select an ${fileType} file.`);
        } else {
          // Revoke previous blob URL before creating a new one to prevent memory leaks
          if (blobURLRef.current) {
            URL.revokeObjectURL(blobURLRef.current);
            blobURLRef.current = null;
          }

          setFile(targetFile);

          if (fileSizeInMB <= previewThreshold) {
            const blobURL = URL.createObjectURL(targetFile);
            blobURLRef.current = blobURL; // Store for cleanup
            setFileURL(blobURL);
          } else {
            // Skip preview for large files to prevent memory exhaustion
            setFileURL('');
          }

          event.target.value = null;
        }
      }
    }
  };

  return {
    file,
    fileURL,
    fileError,
    fileInputRef,
    setFile,
    setFileError,
    handleAttachFile,
    handleRemoveSelectedFile,
    handleFileInputChange,
    validateInteractiveFile,

    ...(multiple && {
      attachedFiles,
      setAttachedFiles,
      removeAttachedFileFromFiles,
      clearAllFiles,
    }),
  };
};

export default useFileInput;
